export default class BaseAttachableListManager {
    constructor(options = {}) {
        this.containerSelector = options.containerSelector || '.attachable-list-container';
        this.$container = $(this.containerSelector);
        this.attachRoute = options.attachRoute;
        this.detachRoute = options.detachRoute;
        this.reorderRoute = options.reorderRoute;
        this.csrfToken = options.csrfToken || $('meta[name="csrf-token"]').attr('content');
        this.enableDragDrop = options.enableDragDrop || false;
        this.reorderAllRoute = options.reorderAllRoute;
        this.dragHandle = options.dragHandle || '.drag-handle';
        this.detachItemSelector = options.detachItemSelector || '.detach-item';
        this.detachDataKey = options.detachDataKey || 'item-id';
        this.notificationContainerId = options.notificationContainerId || 'attachable-list-notifications';
        this.sortableInstance = null;

        if (!this.csrfToken) {
            console.error(`${this.constructor.name}: CSRF token not found`);
        }

        this.init();
    }

    init() {
        this.setupNotificationContainer();
        this.initSortable();
        this.initTooltips();
        this.bindBaseEvents();
    }

    bindBaseEvents() {
        // Subclasses override for domain-specific bindings.
    }

    getListBody() {
        return this.$container.find('tbody')[0];
    }

    setupNotificationContainer() {
        if (!$(`#${this.notificationContainerId}`).length) {
            $('body').append(`<div id="${this.notificationContainerId}" style="position: fixed; top: 20px; right: 20px; z-index: 9999;"></div>`);
        }
    }

    showNotification(message, type = 'success') {
        let alertClass;
        let icon;

        switch (type) {
            case 'success':
                alertClass = 'alert-success';
                icon = 'fa-check';
                break;
            case 'error':
                alertClass = 'alert-danger';
                icon = 'fa-exclamation-triangle';
                break;
            case 'info':
                alertClass = 'alert-info';
                icon = 'fa-info-circle';
                break;
            default:
                alertClass = 'alert-info';
                icon = 'fa-info-circle';
        }

        const notification = $(`
            <div class="alert ${alertClass} alert-dismissible fade in" role="alert" style="margin-bottom: 10px;">
                <i class="fa ${icon}"></i> ${message}
                <button type="button" class="close" data-dismiss="alert">
                    <span>&times;</span>
                </button>
            </div>
        `);

        $(`#${this.notificationContainerId}`).append(notification);

        setTimeout(() => {
            notification.alert('close');
        }, 5000);
    }

    showLoading(element, show = true) {
        if (show) {
            element.prop('disabled', true);
            const originalText = element.html();
            element.data('original-text', originalText);
            element.html('<i class="fa fa-spinner fa-spin"></i> Loading...');
        } else {
            element.prop('disabled', false);
            const originalText = element.data('original-text');
            if (originalText) {
                element.html(originalText);
            }
        }
    }

    confirmAction(message, confirmText = 'Yes') {
        return Swal.fire({
            text: message,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: confirmText,
            cancelButtonText: 'Cancel',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'btn btn-warning',
                cancelButton: 'btn btn-default',
            },
        }).then((result) => result.isConfirmed);
    }

    makeRequest(url, method = 'GET', formData = {}, successCallback, failureCallback) {
        if (!url) {
            console.error(`${this.constructor.name}: No URL provided for request`);
            failureCallback({ responseText: 'No URL provided' });
            return;
        }

        $.ajax({
            url,
            method,
            data: formData,
            headers: {
                'X-CSRF-TOKEN': this.csrfToken,
            },
            success: successCallback,
            error: failureCallback,
        });
    }

    extractErrorMessage(xhr) {
        try {
            const response = JSON.parse(xhr.responseText);
            return response.message || response.error || 'Unknown error';
        } catch (e) {
            return xhr.responseText || 'Network error';
        }
    }

    getItemIdFromRow($row) {
        return $row.find(this.detachItemSelector).data(this.detachDataKey);
    }

    collectOrderedIds() {
        const orderedIds = [];
        this.$container.find('tbody tr').each((_, row) => {
            const itemId = this.getItemIdFromRow($(row));
            if (itemId) {
                orderedIds.push(itemId);
            }
        });

        return orderedIds;
    }

    updateOrderNumbers() {
        this.$container.find('tbody tr').each(function (index) {
            $(this).find('td[field-key="ordered"]').text(index + 1);
        });
    }

    initSortable() {
        if (!this.enableDragDrop) {
            return;
        }

        if (typeof Sortable === 'undefined') {
            console.error(`${this.constructor.name}: SortableJS library not loaded`);
            return;
        }

        const tbody = this.getListBody();
        if (!tbody) {
            console.warn(`${this.constructor.name}: table tbody not found`);
            return;
        }

        this.sortableInstance = Sortable.create(tbody, {
            animation: 150,
            handle: this.dragHandle,
            ghostClass: 'sortable-ghost',
            dragClass: 'sortable-drag',
            onEnd: () => this.handleDragEnd(),
        });
    }

    initTooltips() {
        this.$container.find('[data-toggle="tooltip"]').tooltip({
            delay: {
                show: 200,
                hide: 100,
            },
        });
    }

    handleDragEnd() {
        this.updateOrderNumbers();
        this.reorderAllItems(this.collectOrderedIds());
    }

    reorderAllItems(orderedIds) {
        if (!this.reorderAllRoute) {
            this.showNotification('Drag-and-drop reordering is not configured', 'error');
            return;
        }

        if (!orderedIds || orderedIds.length === 0) {
            return;
        }

        this.makeRequest(
            this.reorderAllRoute,
            'PATCH',
            { ordered_ids: orderedIds },
            () => this.showNotification('Order updated successfully!', 'success'),
            (xhr) => {
                const errorMessage = this.extractErrorMessage(xhr);
                this.showNotification(`Failed to reorder: ${errorMessage}`, 'error');
                setTimeout(() => location.reload(), 1500);
            }
        );
    }

    removeItemFromTable(itemId) {
        const row = $(`${this.detachItemSelector}[data-${this.detachDataKey}="${itemId}"]`).closest('tr');
        row.fadeOut(300, () => row.remove());
    }

    static optionsFromContainer($container, defaults = {}) {
        return {
            containerSelector: defaults.containerSelector,
            attachRoute: $container.data('attach-route'),
            detachRoute: $container.data('detach-route'),
            reorderRoute: $container.data('reorder-route'),
            enableDragDrop: $container.data('enable-drag-drop') === 'true' || $container.data('enable-drag-drop') === true,
            reorderAllRoute: $container.data('reorder-all-route'),
            dragHandle: $container.data('drag-handle') || '.drag-handle',
            detachItemSelector: defaults.detachItemSelector || '.detach-item',
            detachDataKey: defaults.detachDataKey || 'item-id',
            notificationContainerId: defaults.notificationContainerId || 'attachable-list-notifications',
            ...defaults.extraOptions,
        };
    }
}
